import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../providers/AuthProvider';
import { api } from './apiClient';

interface MeWithWildplus {
    id: string;
    email: string | null;
    isAdmin: boolean;
    wildplus: {
        active: boolean;
        expiresAt: string | null;
        /// 'stripe' joined the union with Plan §37 S2 — Wild+ sold
        /// on the web funds club organising.
        sources: Array<'apple' | 'google' | 'stripe'>;
    };
    /// Plan §37 S12 — server-driven clubs entry-point visibility.
    /// Optional so a stale cached /v1/me from an older API revision
    /// doesn't crash the shell.
    clubs?: {
        enabled: boolean;
        membershipCount: number;
    };
}

/**
 * Cross-platform Wild+ entitlement check. Reads /v1/me — the same
 * endpoint useIsAdmin uses, so a single round-trip covers both.
 *
 * Returns:
 *   - ready: false while auth/me is loading
 *   - isWildplus: true when the backend confirms an active
 *     Apple, Google OR Stripe entitlement (revoked = no,
 *     expiresAt > now)
 *   - expiresAt: furthest-out expiry across all active rows
 *   - sources: which stores currently grant access
 *   - clubsEnabled / clubMembershipCount: §37 S12 server-driven
 *     Clubs entry-point (flag ∨ member-of-a-club ∨ pilot)
 *
 * Use this to gate paywalled features on web: planner depth,
 * widgets, Swim-Mode, offline cache, journal beyond 12 entries.
 * Since Plan §37 S2 the web DOES sell Wild+ (Stripe Checkout via
 * /clubs) — club organising is the web-purchasable tier; app
 * binaries still carry zero purchase UI for it.
 */
export function useEntitlement() {
    const { isSignedIn, loading: authLoading } = useAuth();
    const q = useQuery({
        queryKey: ['me', 'isAdmin'], // Reuses the same /v1/me query useIsAdmin uses
        queryFn: () => api.get<MeWithWildplus>('/v1/me'),
        enabled: isSignedIn,
        staleTime: 5 * 60 * 1000,
        retry: false,
    });
    const wildplus = q.data?.wildplus ?? { active: false, expiresAt: null, sources: [] };
    return {
        ready: !authLoading && (!isSignedIn || !q.isLoading),
        /** Backend User.id (NOT the Firebase uid) — roster "you" checks. */
        userId: q.data?.id ?? null,
        isWildplus: wildplus.active,
        expiresAt: wildplus.expiresAt,
        sources: wildplus.sources,
        clubsEnabled: q.data?.clubs?.enabled ?? false,
        clubMembershipCount: q.data?.clubs?.membershipCount ?? 0,
    };
}
